/**
 * What a Models-tab change does to the agents it reaches (item 92), as the
 * server's impact check answers it (`POST /super-admin/tenants/<id>/
 * model-families/impact`, and the models apply-to-descendants dry run):
 * who moves to which release, the meta-agent slots that move, agents left
 * with no model at all, and what each new release changes for them.
 *
 * Shown in the confirmation before the change is saved; a change that moves
 * nobody is saved without asking.
 */
import { Typography } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { bidiIsolate } from '@/utils/bidi';

export interface ImpactMove {
  tenant_id: string;
  tenant_name?: string;
  agent_id: string;
  agent_name?: string;
  from: string;
  to: string;
  from_name?: string;
  to_name?: string;
  rewritten?: { allowed: string[] | 'all'; default: string | null; dropped: string[] } | null;
}

export interface ImpactMetaMove {
  tenant_name?: string;
  slot: 'agent_of_agents' | 'concierge' | 'guide';
  from: string;
  to: string;
  from_name?: string;
  to_name?: string;
}

export interface ImpactWarning {
  code: 'smaller_window' | 'no_vision' | 'output_cap' | 'temperature_ignored' | 'search_route' | 'price';
  from: string;
  to: string;
  from_name?: string;
  to_name?: string;
  window_from?: number;
  window_to?: number;
  conversations_compacting?: number;
  agents?: number;
  cap?: number;
  price_from?: { input_per_million: number; output_per_million: number; verified: boolean } | null;
  price_to?: { input_per_million: number; output_per_million: number; verified: boolean } | null;
}

export interface ModelImpact {
  organizations: number;
  agents_checked: number;
  moves: ImpactMove[];
  meta_moves: ImpactMetaMove[];
  warnings: ImpactWarning[];
  blocking: number;
}

/** Anything worth asking about before the change is saved. */
export function impactIsEmpty(impact: ModelImpact | null | undefined): boolean {
  return !impact || (impact.moves.length === 0 && impact.meta_moves.length === 0);
}

const NO_MODEL = 'NO_MODEL';
const SHOWN_PER_GROUP = 8;

const tokens = (n?: number) => (n && n >= 1000 ? `${Math.round(n / 1000)}K` : String(n ?? 0));

export function ModelChangeImpact({ impact }: { impact: ModelImpact }) {
  const { t } = useLanguage();
  const fill = (key: string, values: Record<string, string | number>) =>
    Object.entries(values).reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v)), t(key));
  const release = (id: string, name?: string) =>
    bidiIsolate(id === NO_MODEL ? t('superAdmin.modelImpact.noModel') : (name || id));

  const groups = new Map<string, ImpactMove[]>();
  for (const m of impact.moves) {
    const key = `${m.from}|${m.to}`;
    groups.set(key, [...(groups.get(key) || []), m]);
  }
  const rewritten = impact.moves.filter(m => m.rewritten).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }} data-testid="model-change-impact">
      {impact.blocking > 0 && (
        <Typography.Text type="danger" strong data-testid="model-impact-blocking">
          {fill('superAdmin.modelImpact.blocking', { count: impact.blocking })}
        </Typography.Text>
      )}
      {impact.moves.length > 0 && (
        <div>
          {fill('superAdmin.modelImpact.moves', { count: impact.moves.length, orgs: impact.organizations })}
        </div>
      )}
      {[...groups.values()].map(list => (
        <div key={`${list[0].from}|${list[0].to}`} style={{ paddingInlineStart: 12 }}>
          <div style={{ fontWeight: 600 }}>
            {fill('superAdmin.modelImpact.pair', {
              from: release(list[0].from, list[0].from_name), to: release(list[0].to, list[0].to_name),
            })}
          </div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            {list.slice(0, SHOWN_PER_GROUP).map(m => bidiIsolate(m.agent_name || m.agent_id)).join(', ')}
            {list.length > SHOWN_PER_GROUP && ` ${fill('superAdmin.modelImpact.andMore', { count: list.length - SHOWN_PER_GROUP })}`}
          </div>
        </div>
      ))}
      {rewritten > 0 && (
        <div style={{ fontSize: 12 }}>{fill('superAdmin.modelImpact.rewritten', { count: rewritten })}</div>
      )}
      {impact.meta_moves.length > 0 && (
        <div>
          <div>{t('superAdmin.modelImpact.metaTitle')}</div>
          {impact.meta_moves.map((m, i) => (
            <div key={i} style={{ fontSize: 12, opacity: 0.8, paddingInlineStart: 12 }}>
              {fill('superAdmin.modelImpact.metaMove', {
                slot: t(`superAdmin.modelImpact.slot.${m.slot}`),
                org: bidiIsolate(m.tenant_name || ''),
                from: release(m.from, m.from_name), to: release(m.to, m.to_name),
              })}
            </div>
          ))}
        </div>
      )}
      {impact.warnings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {impact.warnings.map((w, i) => {
            const base = { from: release(w.from, w.from_name), to: release(w.to, w.to_name) };
            let text: string;
            switch (w.code) {
              case 'smaller_window':
                text = fill('superAdmin.modelImpact.warn.smallerWindow', {
                  ...base, windowFrom: tokens(w.window_from), windowTo: tokens(w.window_to),
                  count: w.conversations_compacting ?? 0,
                });
                break;
              case 'output_cap':
                text = fill('superAdmin.modelImpact.warn.outputCap', { ...base, count: w.agents ?? 0, cap: tokens(w.cap) });
                break;
              case 'temperature_ignored':
                text = fill('superAdmin.modelImpact.warn.temperature', { ...base, count: w.agents ?? 0 });
                break;
              case 'price':
                text = fill('superAdmin.modelImpact.warn.price', {
                  ...base,
                  inFrom: w.price_from?.input_per_million ?? '?', outFrom: w.price_from?.output_per_million ?? '?',
                  inTo: w.price_to?.input_per_million ?? '?', outTo: w.price_to?.output_per_million ?? '?',
                }) + (w.price_to && !w.price_to.verified ? ` ${t('superAdmin.modelImpact.warn.priceUnverified')}` : '');
                break;
              default:
                text = fill(`superAdmin.modelImpact.warn.${w.code === 'no_vision' ? 'noVision' : 'searchRoute'}`, base);
            }
            return <div key={i} style={{ fontSize: 12 }}>• {text}</div>;
          })}
        </div>
      )}
    </div>
  );
}
